import React, { createContext, useContext, useState, useEffect } from 'react';

const CompareContext = createContext(null);

export const useCompare = () => {
  const context = useContext(CompareContext);
  if (!context) {
    throw new Error('useCompare must be used within a CompareProvider');
  }
  return context;
};

const COMPARE_KEY = 'conti_compare_properties';
const MAX_COMPARE = 4;

// Helper function to load initial state from localStorage
const getInitialCompareList = () => {
  if (typeof window === 'undefined') return [];
  try {
    const stored = localStorage.getItem(COMPARE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error loading compare list from localStorage:', e);
  }
  return [];
};

export const CompareProvider = ({ children }) => {
  // Initialize directly from localStorage to avoid race condition
  const [compareList, setCompareList] = useState(getInitialCompareList);

  // Save to localStorage whenever compareList changes
  useEffect(() => {
    localStorage.setItem(COMPARE_KEY, JSON.stringify(compareList));
  }, [compareList]);

  const addToCompare = (property) => {
    if (compareList.length >= MAX_COMPARE) {
      return { success: false, message: `You can only compare up to ${MAX_COMPARE} properties` };
    }
    if (compareList.find(p => p.id === property.id)) {
      return { success: false, message: 'Property already in compare list' };
    }
    setCompareList([...compareList, property]);
    return { success: true, message: 'Added to compare' };
  };

  const removeFromCompare = (propertyId) => {
    setCompareList(compareList.filter(p => p.id !== propertyId));
  };

  const clearCompare = () => {
    setCompareList([]);
  };

  const isInCompare = (propertyId) => {
    return compareList.some(p => p.id === propertyId);
  };

  return (
    <CompareContext.Provider value={{
      compareList,
      addToCompare,
      removeFromCompare,
      clearCompare,
      isInCompare,
      maxCompare: MAX_COMPARE
    }}>
      {children}
    </CompareContext.Provider>
  );
};
